import { FC, ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";

interface OptionCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
  last?: boolean;
}

// Same card pattern as ScoringWizardDialog's "choose" step (MethodCard) —
// not shared/exported from there since that component doesn't expose it,
// but deliberately kept visually identical.
const OptionCard: FC<OptionCardProps> = ({
  icon,
  title,
  description,
  testId,
  onClick,
  disabled = false,
  last = false,
}) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
      mb: last ? 0 : 1.5,
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: 2,
      width: "100%",
      textAlign: "left",
      opacity: disabled ? 0.5 : 1,
      "&:hover:not(:disabled)": {
        borderColor: "primary.main",
        bgcolor: "action.hover",
      },
    }}
  >
    <Box sx={{ color: "primary.main", mt: 0.25, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </ButtonBase>
);

interface ExportChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectCompanyList: () => void;
  onSelectMarketSizing: () => void;
  /** Mirrors the existing header Export button's own gating (applied
   * filters) — both exports need at least one filter applied to mean
   * anything. */
  marketSizingAvailable: boolean;
  /** True while handleExportMarketSizing (CompaniesExplorePage) is
   * switching to the tab, waiting for it to settle and downloading —
   * disables both cards, since starting a second export mid-flight would
   * fight the first one over the same tab strip. */
  exportingMarketSizing: boolean;
  /** Stops an export in progress. A Market Sizing export waits on several
   * tabs' worth of queries, so it must always be possible to back out
   * rather than sit on a dialog nothing can dismiss. */
  onCancelExport: () => void;
}

export const ExportChoiceDialog: FC<ExportChoiceDialogProps> = ({
  open,
  onClose,
  onSelectCompanyList,
  onSelectMarketSizing,
  marketSizingAvailable,
  exportingMarketSizing,
  onCancelExport,
}) => (
  <Dialog
    open={open}
    onClose={exportingMarketSizing ? onCancelExport : onClose}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle>Export</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What would you like to export?
      </Typography>

      <OptionCard
        icon={<ListAltOutlinedIcon sx={{ fontSize: 36 }} />}
        title="Company List"
        description="A list of companies based on the filters that were applied."
        testId="export-choice-company-list-btn"
        onClick={onSelectCompanyList}
        disabled={exportingMarketSizing}
      />
      <OptionCard
        icon={
          exportingMarketSizing ? (
            <CircularProgress size={36} />
          ) : (
            <InsertChartOutlinedIcon sx={{ fontSize: 36 }} />
          )
        }
        title="Market Sizing"
        description="The current Market Sizing view as a shareable HTML report."
        testId="export-choice-market-sizing-btn"
        onClick={onSelectMarketSizing}
        disabled={!marketSizingAvailable || exportingMarketSizing}
        last
      />
    </DialogContent>
    <DialogActions>
      <Button
        onClick={exportingMarketSizing ? onCancelExport : onClose}
        data-testid="export-choice-cancel-btn"
      >
        {exportingMarketSizing ? "Cancel export" : "Cancel"}
      </Button>
    </DialogActions>
  </Dialog>
);
